import type { ZodError, ZodIssue } from 'zod';

/**
 * Known refine messages from the quality-settings schema, mapped to their
 * dedicated i18n key. Kept in sync with `qualitySettingsService.ts`.
 *
 * NOTE: The `passiveLow < passiveHigh` entry is DEFENSIVE. That refine is
 * currently commented out in the service (see qualitySettingsService.ts:35-38).
 * The entry lives here so that re-enabling the refine requires zero mapper
 * changes.
 */
const REFINE_MESSAGE_TO_KEY: Readonly<Record<string, string>> = {
  'livenessLiveCount cannot exceed livenessWindowSize':
    'admin.qualityErrors.livenessLiveCountOverWindow',
  'livenessSpoofConsecutive cannot exceed livenessWindowSize':
    'admin.qualityErrors.livenessSpoofConsecutiveOverWindow',
  'passiveLow must be less than passiveHigh':
    'admin.qualityErrors.passiveOrder',
};

const OUT_OF_RANGE_KEY = 'admin.qualityErrors.outOfRange';

/**
 * Convert a ZodError into a per-field map of i18n key strings.
 *
 * Map key = `issue.path.join('.')` (top-level fields stay flat; nested paths
 * become `parent.child`). Value = i18n KEY string. For range issues, the value
 * uses a marker convention so callers can interpolate min/max:
 *
 *     'admin.qualityErrors.outOfRange|min=50|max=800'
 *
 * The caller splits on `|`, extracts the params, and calls `t(key, params)`.
 *
 * Resolution order per issue:
 *   1. Refine-message lookup (custom issues with a known message).
 *   2. Range codes (`too_small`, `too_big`) → outOfRange marker with min/max.
 *   3. Fallback → outOfRange (no min/max marker).
 *
 * Pure: does not mutate the input, no side effects.
 */
export function mapZodErrorsToFieldErrors(err: ZodError): Record<string, string> {
  const result: Record<string, string> = {};

  for (const issue of err.issues) {
    const path = issue.path.join('.');
    result[path] = resolveIssue(issue);
  }

  return result;
}

function resolveIssue(issue: ZodIssue): string {
  // 1. Refine-message lookup — for custom refines with known messages.
  const refineKey = REFINE_MESSAGE_TO_KEY[issue.message];
  if (refineKey) {
    return refineKey;
  }

  // 2. Range codes → outOfRange marker with min/max interpolation.
  if (issue.code === 'too_small') {
    const min = extractNumeric((issue as { minimum?: number | bigint }).minimum);
    return buildOutOfRangeMarker(min, undefined);
  }
  if (issue.code === 'too_big') {
    const max = extractNumeric((issue as { maximum?: number | bigint }).maximum);
    return buildOutOfRangeMarker(undefined, max);
  }

  // 3. Fallback for anything else (unknown custom refines, etc.).
  return OUT_OF_RANGE_KEY;
}

function extractNumeric(value: number | bigint | undefined): number | undefined {
  if (value === undefined) return undefined;
  if (typeof value === 'bigint') return Number(value);
  return value;
}

function buildOutOfRangeMarker(min: number | undefined, max: number | undefined): string {
  const parts: string[] = [OUT_OF_RANGE_KEY];
  if (min !== undefined) parts.push(`min=${String(min)}`);
  if (max !== undefined) parts.push(`max=${String(max)}`);
  return parts.join('|');
}
